import { useEffect, useState } from 'react'

/**
 * One listener per collection, shared by everyone who wants it.
 *
 * Four screens read the decision register — the home page, the register itself, the
 * roadmap and the global search — and each used to open its own `onSnapshot`. While a
 * listener is live Firestore serves an identical second one from the local query cache, so
 * that was free; what was not free was navigating. Leaving the roadmap stops its listener,
 * arriving at the register starts a fresh one, and a fresh listener with nothing to resume
 * from is billed for every document it delivers. On a device where the persistent cache is
 * unavailable — a private window, a browser set to refuse site data, Safari having evicted
 * it — that is the whole collection again on every tab change: about 1,700 documents a lap
 * across the register, the board, the field guide, the products and the owners. Thirty laps
 * is the free day's entire allowance, and thirty laps is an ordinary morning's work.
 *
 * So the subscription outlives the component. The first screen that asks for a collection
 * starts it, later screens attach to the same one, and it is not stopped when the last of
 * them unmounts — it stays for the life of the tab, receiving only what changes, which is
 * what a listener is cheap at. The cost is holding a few thousand small documents in
 * memory, which is the trade this project wants: memory is free here and reads are not.
 *
 * The store is deliberately ignorant of Firestore so it can be tested without one.
 */

export type SharedState<T> = { rows: T[]; loading: boolean; error: Error | null }

type Watcher<T> = (state: SharedState<T>) => void

/** What a store needs in order to start one subscription. Firestore is bound by the caller. */
export type StartSubscription<T> = (
  onRows: (rows: T[]) => void,
  onError: (err: Error) => void,
) => () => void

type Entry<T> = {
  state: SharedState<T>
  watchers: Set<Watcher<T>>
  stop: (() => void) | null
}

export function createSharedStore() {
  const entries = new Map<string, Entry<unknown>>()

  const publish = <T>(entry: Entry<T>) => {
    // A copy per delivery: watchers are React setState calls, and handing them the same
    // object twice means the second render is skipped.
    const snapshot = { ...entry.state }
    for (const watcher of entry.watchers) watcher(snapshot)
  }

  return {
    /**
     * Attaches to the collection under `key`, starting the subscription if this is the
     * first caller. Returns a detach function; detaching does not stop the subscription.
     *
     * `start` is used only by the first caller. Later callers attach to what is already
     * running, so every caller for one key must want the same query — which is why the key
     * names the collection rather than the screen.
     */
    subscribe<T>(key: string, start: StartSubscription<T>, watcher: Watcher<T>): () => void {
      let entry = entries.get(key) as Entry<T> | undefined
      if (!entry) {
        entry = { state: { rows: [], loading: true, error: null }, watchers: new Set(), stop: null }
        entries.set(key, entry as Entry<unknown>)
        const created = entry
        created.stop = start(
          (rows) => { created.state = { rows, loading: false, error: null }; publish(created) },
          (error) => { created.state = { ...created.state, loading: false, error }; publish(created) },
        )
      }
      entry.watchers.add(watcher)
      // Whatever is already known, immediately — an arriving screen should render the rows
      // the last one was looking at rather than an empty list that fills in a frame later.
      watcher({ ...entry.state })
      return () => { entry.watchers.delete(watcher) }
    },

    /** Live subscriptions, by key. For the usage panel and for tests. */
    keys(): string[] { return [...entries.keys()] },

    /** Stops everything. Sign-out only: the rows belong to the person who was signed in. */
    reset(): void {
      for (const entry of entries.values()) { entry.stop?.(); entry.watchers.clear() }
      entries.clear()
    },
  }
}

export const sharedCollections = createSharedStore()

/**
 * The hook every collection hook is built from.
 *
 * `start` is read once per key, by whichever caller gets there first, so it must not close
 * over anything that changes — it builds a query and nothing else. `enabled` is how a
 * caller says it may not read this collection at all: subscribing to something the rules
 * refuse returns a permission error rather than an empty list.
 */
export function useSharedCollection<T>(
  key: string,
  start: StartSubscription<T>,
  enabled = true,
): SharedState<T> {
  const [state, setState] = useState<SharedState<T>>({ rows: [], loading: true, error: null })

  useEffect(() => {
    if (!enabled) { setState({ rows: [], loading: false, error: null }); return }
    return sharedCollections.subscribe<T>(key, start, setState)
    // `start` is deliberately not a dependency: it is a fresh closure on every render and
    // only the first caller's copy is ever used. See the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled])

  return state
}
